/**
 * ExpiredTrialBanner Component
 * Shows a persistent banner when trial has expired or subscription is canceled
 * Encourages upgrade with gentle but visible prompt
 */

import { useState, useEffect } from 'react';
import { Box, Typography, Button, IconButton, Collapse } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import StarIcon from '@mui/icons-material/Star';
import { featureAccess } from '../services/featureAccess';
import { getCyberColors } from '../theme/cyberColors';
import { useAppStore } from '../stores/appStore';
import { useTranslation } from '../i18n/translations';

interface ExpiredTrialBannerProps {
  onUpgrade?: () => void;
}

export function ExpiredTrialBanner({ onUpgrade }: ExpiredTrialBannerProps) {
  const { t } = useTranslation();
  const darkMode = useAppStore((s) => s.darkMode);
  const highContrast = useAppStore((s) => s.highContrast);
  const cyber = getCyberColors(darkMode, highContrast);
  
  const [showBanner, setShowBanner] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [statusText, setStatusText] = useState('');
  
  useEffect(() => {
    const checkStatus = async () => {
      // Don't show if user already dismissed this session
      const dismissedThisSession = sessionStorage.getItem('trialBannerDismissed');
      if (dismissedThisSession) {
        setDismissed(true);
        return;
      }
      
      const summary = await featureAccess.getStatusSummary();
      
      if (summary.showExpiredBanner) {
        setShowBanner(true);
        setStatusText(summary.statusText);
      } else if (summary.showTrialBanner) {
        // Show warning when trial is almost up
        setShowBanner(true);
        setStatusText(`⏰ ${summary.statusText}`);
      }
    };
    
    checkStatus();
    
    // Re-check every minute
    const interval = setInterval(checkStatus, 60000);
    return () => clearInterval(interval);
  }, []);
  
  const handleDismiss = () => {
    setDismissed(true);
    sessionStorage.setItem('trialBannerDismissed', 'true');
  };
  
  const handleUpgrade = () => {
    if (onUpgrade) {
      onUpgrade();
    } else {
      // Dispatch event for VoiceNavigation to handle
      window.dispatchEvent(new CustomEvent('openSubscription'));
    }
  };
  
  if (!showBanner || dismissed) return null;
  
  const isExpired = statusText.includes('Expired') || statusText.includes('Canceled');
  
  return (
    <Collapse in={showBanner && !dismissed}>
      <Box
        sx={{
          background: isExpired 
            ? `linear-gradient(90deg, ${cyber.neonGold}15, ${cyber.neonGold}25, ${cyber.neonGold}15)`
            : `linear-gradient(90deg, ${cyber.neonBlue}15, ${cyber.neonBlue}25, ${cyber.neonBlue}15)`,
          borderBottom: `1px solid ${isExpired ? cyber.neonGold : cyber.neonBlue}40`,
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          position: 'relative',
          zIndex: 1000,
        }}
      >
        <StarIcon sx={{ 
          color: isExpired ? cyber.neonGold : cyber.neonBlue, 
          fontSize: 20,
          animation: isExpired ? 'pulse 2s infinite' : 'none',
          '@keyframes pulse': {
            '0%, 100%': { opacity: 1 },
            '50%': { opacity: 0.5 }
          }
        }} />
        
        <Typography 
          variant="body2" 
          sx={{ 
            color: isExpired ? cyber.neonGold : cyber.textPrimary,
            fontWeight: 600,
            fontSize: '0.875rem',
          }}
        >
          {isExpired ? t('trialEnded') : t('trialEndingSoon', statusText)}
        </Typography>
        
        <Button
          variant="contained"
          size="small"
          onClick={handleUpgrade}
          sx={{
            background: isExpired 
              ? `linear-gradient(135deg, ${cyber.neonGold}, #f59e0b)`
              : `linear-gradient(135deg, ${cyber.neonBlue}, #3b82f6)`,
            color: '#000',
            fontWeight: 700,
            fontSize: '0.75rem',
            px: 2,
            py: 0.5,
            borderRadius: '20px',
            textTransform: 'none',
            boxShadow: `0 2px 8px ${isExpired ? cyber.neonGold : cyber.neonBlue}40`,
            '&:hover': {
              background: isExpired 
                ? `linear-gradient(135deg, #f59e0b, ${cyber.neonGold})`
                : `linear-gradient(135deg, #3b82f6, ${cyber.neonBlue})`,
              transform: 'scale(1.05)',
            }
          }}
        >
          {t('upgradeNow')}
        </Button>
        
        <IconButton
          size="small"
          onClick={handleDismiss}
          sx={{ 
            position: 'absolute',
            right: 8,
            color: cyber.textMuted,
            '&:hover': { color: cyber.textPrimary }
          }}
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Collapse>
  );
}
